import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailLink,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  type Auth,
  type User,
  type ConfirmationResult,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

function getFirebaseApp(): FirebaseApp {
  if (getApps().length > 0) {
    return getApps()[0]!;
  }
  return initializeApp(firebaseConfig);
}

export const app = getFirebaseApp();
export const auth: Auth = getAuth(app);

let recaptchaVerifier: RecaptchaVerifier | null = null;

export function setupRecaptcha(containerId: string): RecaptchaVerifier {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      /* ignore */
    }
  }
  recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
  });
  return recaptchaVerifier;
}

export async function signInWithGoogle(): Promise<{ user: User }> {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return { user: result.user };
}

export async function loginWithEmail(email: string, password: string): Promise<{ user: User }> {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return { user: result.user };
  } catch (error: any) {
    if (error.code === 'auth/user-not-found') {
      throw new Error('user-not-found');
    }
    if (error.code === 'auth/invalid-credential') {
      throw new Error('invalid-credential');
    }
    throw error;
  }
}

export async function signupWithEmail(email: string, password: string, name?: string): Promise<{ user: User }> {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      await updateProfile(result.user, { displayName: name });
    }
    await result.user.reload();
    return { user: auth.currentUser || result.user };
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('email-already-in-use');
    }
    throw error;
  }
}

export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    if (error.code === 'auth/user-not-found') {
      throw new Error('user-not-found');
    }
    throw error;
  }
}

export async function sendEmailOTP(email: string): Promise<void> {
  const actionCodeSettings = {
    url: `${window.location.origin}/verify-email`,
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  window.localStorage.setItem("emailForSignIn", email);
}

export async function verifyEmailOTP(emailLink: string): Promise<{ user: User }> {
  if (!isSignInWithEmailLink(auth, emailLink)) {
    throw new Error("Invalid email sign-in link");
  }
  let email = window.localStorage.getItem("emailForSignIn");
  if (!email) {
    email = window.prompt("Please provide your email for confirmation") || "";
  }
  if (!email) {
    throw new Error("Email is required to complete sign-in");
  }
  const result = await signInWithEmailLink(auth, email, emailLink);
  window.localStorage.removeItem("emailForSignIn");
  return { user: result.user };
}

export async function sendPhoneOTP(phoneNumber: string): Promise<ConfirmationResult> {
  if (!recaptchaVerifier) {
    setupRecaptcha("recaptcha-container");
  }
  return signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier!);
}

export async function verifyPhoneOTP(
  confirmationResult: ConfirmationResult,
  otp: string
): Promise<{ user: User }> {
  const result = await confirmationResult.confirm(otp);
  return { user: result.user };
}

export function getCurrentUser(): User | null {
  return auth.currentUser;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

export function onAuthStateChanged(
  callback: (user: User | null) => void | Promise<void>
): () => void {
  return firebaseOnAuthStateChanged(auth, callback);
}
